import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Grid } from '@/UI/Grid/Grid';
import { toMillis } from '@/utils/folder.utils';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenHorVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import WorkflowCardDisplay from '../components/WorkflowCardDisplay';
import { DashboardCard } from '../DashboardCard';
import type { ArchiveItem } from './archive.types';

const styles = stylex.create({
  // Non-interactive archive container: replaces the transparent styled(Card) with a plain box.
  container: {
    background: 'none',
    maxWidth: 345,
  },
});

interface ArchiveWorkflowCardProps {
  item: ArchiveItem;
  onRestore: (id: string, type: 'workflow' | 'folder') => void;
  isRestoring?: boolean;
}

const ArchiveWorkflowCard = ({ item, onRestore, isRestoring }: ArchiveWorkflowCardProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const body = (
    <>
      <WorkflowCardDisplay
        poster={item.poster}
        forceShowOverlay={isRestoring}
        hoverOverlay={
          <FlexCenHorVer
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: color.Black40_T,
            }}
          >
            <ButtonContained
              size="small"
              loading={isRestoring}
              onClick={() => onRestore(item.id, item.itemType)}
              sx={{ pointerEvents: isRestoring ? 'none' : 'auto' }}
            >
              {t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.RESTORE)}
            </ButtonContained>
          </FlexCenHorVer>
        }
      />
      <FlexCol sx={{ pl: 1, pt: 1, pb: 3, pr: 2 }}>
        <TextStdRg noWrap text={item.name} color={token.text} />
        <TextSmRg
          noWrap
          text={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.DELETED_AT, {
            date: getTimeDiffInWords(toMillis(item.deletedAt)),
          })}
          color={token.textSecondary}
        />
      </FlexCol>
    </>
  );

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
      {isFplMigrationEnabled === true ? (
        <div {...stylex.props(styles.container)}>{body}</div>
      ) : (
        <DashboardCard>{body}</DashboardCard>
      )}
    </Grid>
  );
};

export default ArchiveWorkflowCard;
